import { Auth, Storage as S3 } from "aws-amplify";
import { Component, FunctionComponent, useEffect, useState } from "react";
import {
    Col,
    Container,
    Row,
    Form,
    Alert,
    Badge,
    ButtonGroup,
    ToggleButton,
    InputGroup,
} from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import {
    Product as ProductDto,
    Category,
    ProductVariant,
    ProductVariantOverride,
    GetProductInput,
    UpdateProductInput,
    UpdateProductOutput,
    GetProductOutput,
    UpdateProductLifecycleInput,
    OptionChoice,
} from "../../client/core";
import { Color, Path, Storage } from "../../env";
import { Button, icon, variant } from "../form/Button";
import ReactMarkdown from "react-markdown";
import MDEditor from "@uiw/react-md-editor";
import { Typeahead } from "react-bootstrap-typeahead";

export interface ProductState {
    product: ProductDto;
    editProduct: ProductDto;
    productImage?: File;
    activity: Activity;
    error: string;
}

export enum Activity {
    View,
    Archived,
    Edit,
}

export enum ProductAttribute {
    Name,
    Price,
    UOM,
    Volume,
    DetailName,
    DetailDescription,
    VariantOption,
    VariantChoice,
    VariantChoiceAdditionalPrice,
    VariantOverride,
    Category,
    CustomerInputMessage,
    CustomerInputImage
}

export interface ProductProps extends RouteComponentProps {
    productID: string;
    getProduct: (input: GetProductInput) => Promise<GetProductOutput>;
    updateProduct: (input: UpdateProductInput) => Promise<UpdateProductOutput>;
    updateProductLifecycle: (input: UpdateProductLifecycleInput) => Promise<UpdateProductOutput>;
    auth: typeof Auth;
    imageStore: typeof S3;
    categories: Category[];
    onImageUpdate: (productID: string, image: File) => void;
    onInfoUpdate: (product: ProductDto) => void;
    onClose: () => void;
}

export default class Product extends Component<ProductProps, ProductState> {
    constructor(props: ProductProps) {
        super(props);
        this.state = {
            product: {
                name: "",
                price: 0.0,
                disabled: false,
            },
            editProduct: {
                name: "",
                price: 0.0,
                disabled: false,
            },
            error: "",
            activity: Activity.View,
        };
    }

    componentDidMount() {
        this.getProduct()
    }

    changeActivity = (activity: Activity) => {
        this.setState({
            activity: activity,
        });
    };

    handleChange = (attribute: ProductAttribute, value: any, index?: number, subindex?: number) => {
        let product = this.state.editProduct;
        switch (attribute) {
            case ProductAttribute.Name:
                product.name = value;
                break;
            case ProductAttribute.Price:
                product.price = value;
                break;
            case ProductAttribute.UOM:
                product.uom = value;
                break;
            case ProductAttribute.Volume:
                product.volume = value;
                break;
            case ProductAttribute.Category:
                product.category = value;
                break;
            case ProductAttribute.CustomerInputImage:
                if (!product.customerInput) {
                    product.customerInput = {
                        image: false,
                        message: false,
                    }
                }
                product.customerInput!.image = Boolean(value)
                break;
            case ProductAttribute.CustomerInputMessage:
                if (!product.customerInput) {
                    product.customerInput = {
                        image: false,
                        message: false,
                    }
                }
                product.customerInput!.message = Boolean(value)
                break;
            case ProductAttribute.DetailName:
                product.details![index!].name = value
                break;
            case ProductAttribute.DetailDescription:
                product.details![index!].description = value
                break;
            case ProductAttribute.VariantOption:
                product.variants![index!].name = value
                break;
            case ProductAttribute.VariantChoice:
                product.variants![index!].choices[subindex!].name = value
                break;
            case ProductAttribute.VariantChoiceAdditionalPrice:
                product.variants![index!].choices[subindex!].additionalCharge = value
                break;
        }
        this.setState({
            editProduct: product,
        });
    };

    handleVariantOverrideChange = (key: string, override: ProductVariantOverride) => {
        let overrides = this.state.editProduct.variantOverrides

        if (!overrides) {
            overrides = new Map<string, ProductVariantOverride>()
        }

        if (!override.price || override.price === 0) {
            overrides?.delete(key)
            return this.setState({
                editProduct: {
                    ...this.state.editProduct,
                    variantOverrides: overrides,
                }
            })
        }

        overrides?.set(key, override)
        this.setState({
            editProduct: {
                ...this.state.editProduct,
                variantOverrides: overrides,
            }
        })
    }

    handleAddDetail = () => {
        let product = this.state.editProduct;
        if (!product.details) {
            product.details = []
        }
        product.details?.push({
            name: "",
            description: "",
        })
        this.setState({
            editProduct: product,
        })
    }

    handleAddVariant = () => {
        let product = this.state.editProduct;
        if (!product.variants) {
            product.variants = []
        }
        product.variants?.push({
            name: "",
            choices: [{
                additionalCharge: 0,
                name: ""
            }]
        })
        this.setState({
            editProduct: product,
        })
    }

    handleAddVariantOption = (index: number) => {
        let product = this.state.editProduct;
        product.variants![index].choices.push({
            additionalCharge: 0,
            name: "",
        })
        this.setState({
            editProduct: product,
        })
    }

    handleImageChange = (image: File) => {
        this.setState({
            productImage: image,
        });
    };

    handleSave = () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
        }

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            const product: ProductDto = this.cleanProduct(this.state.editProduct)
            this.props.updateProduct({
                product: product,
                identityToken: token,
                shopID: shopID!,
            })
                .then(async (output) => {
                    if (output.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        return this.setState({
                            error: "You were forbidden to update shop products. Please navigate back to the shop dashboard.",
                        });
                    }

                    this.props.onInfoUpdate(product);

                    if (this.state.productImage) {
                        const imageName =
                            localStorage.getItem(Storage.ShopID) +
                            "/products/" +
                            Date.now().toString() + "/" +
                            product.id!

                        await this.props.imageStore.put(
                            imageName,
                            this.state.productImage!,
                            {
                                contentType: "image/jpeg",
                            }
                        );

                        this.props.onImageUpdate(
                            this.props.productID,
                            this.state.productImage
                        );
                    }

                    this.setState({
                        activity: Activity.View,
                    });
                })
                .catch((err) =>
                    this.setState({
                        error: err.message,
                    })
                );
        });
    };

    cleanProduct = (product: ProductDto): ProductDto => {
        product.price = Number(product.price)
        // Remove variants without a name and remove options with no value
        let variants: ProductVariant[] | undefined = product.variants
        if (variants) {
            variants = variants.filter(variant => {
                if (!variant.name || variant.name.trim() === "") {
                    return false
                }

                return true
            }).map(variant => {
                variant.choices = variant.choices.filter(choice => (choice && choice.name.trim() !== "")).map(choice => {
                    return {
                        ...choice,
                        additionalCharge: Number(choice.additionalCharge),
                    }
                })
                return variant
            })
        }
        product.variants = variants

        return product
    }

    handleSelect = (productID: string) => {
        this.props.history.push(Path.Products + "/" + productID);
    };

    getProduct = () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
        }

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.getProduct({
                productID: this.props.productID,
                identityToken: token,
                shopID: shopID!,
            })
                .then((output) => {
                    if (output.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        return this.setState({
                            error: "You were forbidden to list shop products. Please navigate back to the shop dashboard.",
                        });
                    }

                    if (!output.product) {
                        return this.setState({
                            error: "This products details could not be retrieved at this time.",
                        });
                    }

                    // Order product variants alphabetically 
                    if (output.product.variants) {
                        output.product.variants = output.product.variants.sort((a, b) => a.name.localeCompare(b.name))
                        output.product.variants.map(variant => {
                            if (!variant.choices || variant.choices.length === 0) {
                                return variant
                            }
                            variant.choices = variant.choices.sort((a, b) => a.name.localeCompare(b.name))
                            return variant
                        })
                    }

                    this.setState({
                        product: output.product,
                        editProduct: output.product,
                    });
                })
                .catch((err) =>
                    this.setState({
                        error: err.message,
                    })
                );
        });
    };

    handleArchive = () => {
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                error: "Shop could not be found. Please navigate back to the shop dashboard to resync",
            });
        }

        this.props.auth.currentSession().then(session => {
            if (!this.state.product.id) {
                return this.setState({
                    error: "We were unable to identify this product so it was not put into your archive."
                })
            }
            this.props.updateProductLifecycle({
                identityToken: session.getIdToken().getJwtToken(),
                lifecycle: "Archived",
                productID: this.state.product.id,
                shopID: shopID
            }).then(response => {
                if (response.statusCode !== 204) {
                    return this.setState({
                        error: "We were unable to archive this product at this time. If this problem persists please contact support@stumbled.online."
                    })
                }
                this.setState({
                    activity: Activity.Archived
                })
            })
        })
    }

    render() {
        switch (this.state.activity) {
            case Activity.View:
                const viewProps: ProductDisplayProps = {
                    product: this.state.product,
                    categories: this.props.categories,
                    changeActivity: this.changeActivity,
                    onArchive: this.handleArchive,
                    error: this.state.error,
                };
                return <ViewProductDisplay {...viewProps} />;
            case Activity.Archived:
                const archiveProductProps: ArchivedProductProps = {
                    product: this.state.product,
                    onClose: this.props.onClose,
                }
                return <ArchivedProduct {...archiveProductProps} />
            default:
                const editProps: ProductDisplayProps = {
                    product: this.state.editProduct,
                    categories: this.props.categories,
                    changeActivity: this.changeActivity,
                    saveChanges: this.handleSave,
                    onChange: this.handleChange,
                    addImage: this.handleImageChange,
                    onProductDetailAdd: this.handleAddDetail,
                    onProductVariantAdd: this.handleAddVariant,
                    onProductVariantOptionAdd: this.handleAddVariantOption,
                    onProductVariantOverrideChange: this.handleVariantOverrideChange,
                    error: this.state.error,
                };
                return <EditProductDisplay {...editProps} />;
        }
    }
}

export interface ArchivedProductProps {
    product: ProductDto;
    onClose: () => void;
}

export const ArchivedProduct: FunctionComponent<ArchivedProductProps> = (
    props
) => (
    <Container fluid>
        <Row>
            <Col>
                <p>Product was archived successfully. You can restore the product by viewing your archived products under 'Actions', finding the associated product and clicking the 'Restore' button.</p>
            </Col>
        </Row>
        <Row>
            <Col>
                <Button
                    name={"OK"}
                    buttonvariant={variant.Primary}
                    id="archive-product"
                    style={{ float: "right" }}
                    onClick={() => props.onClose()}
                />
            </Col>
        </Row>
    </Container>
)

export interface ProductDisplayProps {
    product: ProductDto;
    productImage?: File;
    error: string;
    categories: Category[];
    changeActivity: (activity: Activity) => void;
    onChange?: (attribute: ProductAttribute, value: any, index?: number, subindex?: number) => void;
    addImage?: (image: File) => void;
    onProductDetailAdd?: () => void;
    onProductVariantAdd?: () => void;
    onProductVariantOptionAdd?: (index: number) => void;
    onProductVariantOverrideChange?: (key: string, variant: ProductVariantOverride) => void
    onArchive?: () => void;
    saveChanges?: () => void;
}

export const ViewProductDisplay: FunctionComponent<ProductDisplayProps> = (
    props
) => (
    <Container fluid>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <Row>
            <Col>
                <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                    <Col xs={3}>
                        <span>Name</span>
                    </Col>
                    <Col>
                        <span>{props.product.name}</span>
                    </Col>
                </Row>
                <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                    <Col xs={3}>
                        <span>Price</span>
                    </Col>
                    <Col>
                        <span>£{Number(props.product.price).toFixed(2)}</span>
                    </Col>
                </Row>
                {(props.product.volume !== 0) && (
                    <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Units</span>
                        </Col>
                        <Col>
                            <span>{props.product.volume!}</span>
                        </Col>
                    </Row>
                )}
                {props.product.uom && (
                    <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>UOM</span>
                        </Col>
                        <Col>
                            <span>{props.product.uom!}</span>
                        </Col>
                    </Row>
                )}
                {props.product.category && (
                    <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Category</span>
                        </Col>
                        <Col>
                            <span>{props.product.category!}</span>
                        </Col>
                    </Row>
                )}
                {props.product.details ? (
                    <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Details</span>
                        </Col>
                        <Col>
                            {props.product.details.map(detail => (
                                <Row key={`product-details-${detail.name}`}>
                                    {(detail.name !== "") && (
                                        <>
                                            <Col xs={12} sm={4} lg={3}>{detail.name}:</Col>
                                            <Col>
                                                <ReactMarkdown>
                                                    {detail.description}
                                                </ReactMarkdown>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            ))}
                        </Col>
                    </Row>
                ) : (
                    <Row style={{ borderBottom: "1px lightgrey solid", marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Details</span>
                        </Col>
                        <Col>
                            <span>No details</span>
                        </Col>
                    </Row>
                )}
                {props.product.variants ? (
                    <Row style={{ marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Variants</span>
                        </Col>
                        <Col>
                            {props.product.variants.map(detail => (
                                <Row key={`product-variant-${detail.name}`}>
                                    <>
                                        <Col xs={12} sm={4} lg={3}>{detail.name}</Col>
                                        <Col>
                                            {detail.choices.map(choice => (
                                                <Badge key={`product-variant-option-${detail.name}-${choice}`} bg="secondary" style={{ background: Color.Primary, padding: ".5rem", color: Color.White, margin: ".1rem" }}>{choice.name}: + £{Number(Number(choice.additionalCharge) / 100).toFixed(2)}</Badge>
                                            ))}
                                        </Col>
                                    </>
                                </Row>
                            ))}
                        </Col>
                    </Row>
                ) : (
                    <Row style={{ marginBottom: ".5rem", padding: ".5rem" }}>
                        <Col xs={3}>
                            <span>Variants</span>
                        </Col>
                        <Col>
                            <span>No variants</span>
                        </Col>
                    </Row>
                )}
            </Col>
            <Col xs={2}>
                <Button
                    name={"Edit"}
                    buttonvariant={variant.Primary}
                    id="edit-product"
                    style={{ float: "right" }}
                    onClick={() => props.changeActivity(Activity.Edit)}
                />
                <Button
                    name={"Archive"}
                    buttonvariant={variant.Primary}
                    id="archive-product"
                    style={{ float: "right" }}
                    onClick={() => props.onArchive!()}
                />
            </Col>
        </Row>
    </Container >
);

export enum EditProductMode {
    Product,
    Variants,
}

export interface EditProductState {
    ImageName: string;
    Mode: EditProductMode;
    IsCustomUnits: boolean;
}

export class EditProductDisplay extends Component<
    ProductDisplayProps,
    EditProductState
> {
    constructor(props: ProductDisplayProps) {
        super(props);
        this.state = {
            ImageName: "Select Image",
            Mode: EditProductMode.Product,
            IsCustomUnits: (this.props.product.uom !== undefined),
        };
    }

    handleModeChange = (mode: EditProductMode): void => {
        this.setState({
            Mode: mode,
        })
    }

    render() {
        return (
            <Container fluid>
                <Row style={{ marginBottom: "1rem" }}>
                    <Col style={{ textAlign: "right" }}>
                        <Button
                            id="cancel-product"
                            buttonvariant={variant.Secondary}
                            name="Cancel"
                            onClick={() => this.props.changeActivity(Activity.View)}
                        />
                        <Button
                            buttonvariant={variant.Primary}
                            name={"Save"}
                            id="submit-product"
                            onClick={() => this.props.saveChanges!()}
                        />
                    </Col>
                </Row>
                <div>
                    {(this.state.Mode == EditProductMode.Product) && <Form>
                        <Alert variant={"danger"} show={this.props.error != ""}>
                            {this.props.error}
                        </Alert>
                        <Row style={{ marginBottom: "1rem" }}>
                            <h5 style={{ textAlign: "center", color: Color.Primary }}>Image</h5>
                            <Col>
                                <div className="input-group mb-3">
                                    <div className="custom-file" style={{ width: "100%" }}>
                                        <input
                                            type="file"
                                            className="form-control"
                                            id="inputGroupFile"
                                            accept="image/jpeg"
                                            style={{
                                                display: "block",
                                                width: "100%"
                                            }}
                                            onChange={(e) => {
                                                this.props.addImage!(e.target.files![0]);
                                                this.setState({
                                                    ImageName: e.target.files![0].name,
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: "1rem" }}>
                            <h5 style={{ textAlign: "center", color: Color.Primary }}>Summary</h5>
                            <Col xs={12} md={6}>
                                <div className="form-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name={"name"}
                                        value={this.props.product.name}
                                        id={"name"}
                                        placeholder="Name"
                                        alt="Product Name Textbox"
                                        onChange={(e) =>
                                            this.props.onChange!(
                                                ProductAttribute.Name,
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            </Col>
                            <Col xs={12} md={6}>
                                <div className="input-group mb-3">
                                    <Typeahead
                                        labelKey="category"
                                        style={{ flex: "1 1 auto", border: 0 }}
                                        id={"category"}
                                        placeholder="Category"
                                        defaultInputValue={this.props.product.category}
                                        onInputChange={value =>
                                            this.props.onChange!(
                                                ProductAttribute.Category,
                                                value as string,
                                            )
                                        }
                                        onChange={value =>
                                            this.props.onChange!(
                                                ProductAttribute.Category,
                                                value[0] as string,
                                            )
                                        }
                                        options={(this.props.categories) ? this.props.categories.map(category => category.name) : []}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="input-group mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        name={"price"}
                                        id={"price"}
                                        value={this.props.product.price}
                                        alt="Product Price Textbox"
                                        placeholder="Price"
                                        onChange={(e) =>
                                            this.props.onChange!(
                                                ProductAttribute.Price,
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            </Col>
                            {(this.state.IsCustomUnits) && (
                                <>
                                    <Col xs={12} md={3}>
                                        <div className="input-group mb-3">
                                            <input
                                                className={"form-control"}
                                                name={"volume"}
                                                id={"volume"}
                                                onChange={(e) =>
                                                    this.props.onChange!(
                                                        ProductAttribute.Volume,
                                                        e.target.value
                                                    )
                                                }
                                                value={this.props.product.volume}
                                                placeholder="Quantity"
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <div className="input-group mb-3">
                                            <input
                                                className={"form-control"}
                                                name={"uom"}
                                                id={"uom"}
                                                value={this.props.product.uom}
                                                onChange={(e) =>
                                                    this.props.onChange!(
                                                        ProductAttribute.UOM,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Units"
                                            />
                                        </div>
                                    </Col>
                                </>
                            )}
                            <Col xs={12}>
                                <div className="input-group mb-3">
                                    <Form.Check
                                        inline
                                        label="Set price using custom units"
                                        name="is-custom-units"
                                        type={"checkbox"}
                                        id={"is-custom-units"}
                                        onChange={(e) => {
                                            this.setState({
                                                IsCustomUnits: e.target.checked,
                                            })
                                        }}
                                        checked={this.state.IsCustomUnits}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: "1rem" }}>
                            <h5 style={{ textAlign: "center", color: Color.Primary }}>Customer Input</h5>
                            <div style={{ textAlign: "center" }}>
                                <p>Allow customers can add special instructions when adding this product to their basket</p>
                                <Col xs={12} className="d-flex justify-content-center">
                                    <div style={{ padding: "1rem" }}>
                                        <Form.Check
                                            type="switch"
                                            label="Message"
                                            name="customer-input-message"
                                            value={(this.props.product.customerInput?.message) ? 1 : 0}
                                            onChange={() => this.props.onChange!(ProductAttribute.CustomerInputMessage, !this.props.product.customerInput?.message)}
                                        />
                                    </div>
                                    <div style={{ padding: "1rem" }}>
                                        <Form.Check
                                            type="switch"
                                            label="Image"
                                            name="customer-input-image"
                                            value={(this.props.product.customerInput?.image) ? 1 : 0}
                                            onChange={() => this.props.onChange!(ProductAttribute.CustomerInputImage, !this.props.product.customerInput?.image)}
                                        />
                                    </div>
                                </Col>
                            </div>
                        </Row>
                        <Row style={{ marginBottom: "1rem" }}>
                            <h5 style={{ textAlign: "center", color: Color.Primary }}>Details</h5>
                            {(this.props.product.details) && this.props.product.details.map((detail, index) => (
                                <ProductDetailForm index={index} key={`product-details-form-${index}-wrapper`} name={detail.name} description={detail.description} onChange={((attr: ProductAttribute, value: any) => this.props.onChange!(attr, value, index))} />
                            ))}
                            <Col style={{ textAlign: "right" }}>
                                <Button buttonvariant={variant.Primary} icon={icon.Plus} onClick={() => this.props.onProductDetailAdd!()} />
                            </Col>
                        </Row>
                        <Row style={{ marginBottom: "1rem" }}>
                            <h5 style={{ textAlign: "center", color: Color.Primary }}>Variants</h5>
                            {(this.props.product.variants) && this.props.product.variants.map((variant, index) => (
                                <ProductVariantForm index={index} key={`product-variant-form-${index}-wrapper`} name={variant.name} options={variant.choices} onOptionAdd={() => this.props.onProductVariantOptionAdd!(index)} onChange={((attr: ProductAttribute, value: any, optionIndex?: number) => this.props.onChange!(attr, value, index, optionIndex))} />
                            ))}
                            <Col xs={12} style={{ textAlign: "right", marginTop: ".5rem" }}>
                                <Button buttonvariant={variant.Primary} icon={icon.Plus} onClick={() => this.props.onProductVariantAdd!()} />
                            </Col>
                        </Row>
                    </Form>}
                </div>
            </Container >
        );
    }
}

export interface ProductDetailFormProps {
    index: number;
    name: string
    description: string
    onChange?: (attribute: ProductAttribute, value: any) => void;
}

export const ProductDetailForm: FunctionComponent<ProductDetailFormProps> = (props) => (
    <div style={{ marginBottom: "2rem" }}>
        <div style={{ position: "relative", display: "flex" }}>
            {/* Left bar with number */}
            <div
                style={{
                    width: "2rem",
                    background: Color.Primary,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                }}
            >
                {props.index + 1}
            </div>

            {/* Content area */}
            <div style={{ flex: 1, paddingLeft: "1rem" }}>
                <Col xs={12}>
                    <Form.Group>
                        <Form.Control
                            type="text"
                            placeholder="Section"
                            name="detail-name"
                            id="detail-name"
                            value={props.name}
                            onChange={(e) =>
                                props.onChange!(ProductAttribute.DetailName, e.target.value)
                            }
                        />
                    </Form.Group>
                </Col>
                <Col xs={12}>
                    <MDEditor
                        style={{ background: Color.White, border: 0, boxShadow: "0" }}
                        color={Color.White}
                        value={props.description}
                        onChange={(value) =>
                            props.onChange!(ProductAttribute.DetailDescription, value)
                        }
                    />
                </Col>
            </div>
        </div>
    </div>
)

export interface ProductVariantFormProps {
    index: number;
    name: string
    options: OptionChoice[]
    onChange: (attribute: ProductAttribute, value: any, optionIndex?: number) => void;
    onOptionAdd: () => void;
}

export const ProductVariantForm: FunctionComponent<ProductVariantFormProps> = (props) => (
    <div style={{ display: "flex", marginBottom: "2rem" }}>
        {/* Left number bar */}
        <div
            style={{
                width: "2rem",
                background: Color.Primary,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
            }}
        >
            {props.index + 1}
        </div>

        {/* Content area */}
        <div style={{ flex: 1, paddingLeft: "1rem" }}>
            <Row >
                <Col xs={12} style={{ paddingTop: ".5rem" }}>
                    <Form.Group>
                        <Form.Label style={{ fontWeight: "bold", color: Color.DarkGrey }}>
                            Option
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="variant-name"
                            id="variant-name"
                            placeholder="Name"
                            value={props.name}
                            onChange={(e) =>
                                props.onChange(ProductAttribute.VariantOption, e.target.value)
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row style={{ paddingTop: ".5rem" }}>
                <Col xs={7}>
                    <Form.Label style={{ fontWeight: "bold", color: Color.DarkGrey }}>
                        Choice
                    </Form.Label>
                </Col>
                <Col xs={5}>
                    <Form.Label style={{ fontWeight: "bold", color: Color.DarkGrey }}>
                        Additional Charge
                    </Form.Label>
                </Col>
            </Row>

            <Row >
                <Col xs={12}>
                    {props.options.map((option, index) => (
                        <Row key={`product-variant-option-${props.name}-${index}`}>
                            <Col xs={7}>
                                <Form.Group>
                                    <Form.Control
                                        type="text"
                                        name="variant-name"
                                        id="variant-name"
                                        placeholder="Option"
                                        value={option.name}
                                        onChange={(e) =>
                                            props.onChange!(
                                                ProductAttribute.VariantChoice,
                                                e.target.value,
                                                index
                                            )
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={5}>
                                <PriceInput
                                    onChange={(value) =>
                                        props.onChange!(
                                            ProductAttribute.VariantChoiceAdditionalPrice,
                                            value,
                                            index
                                        )
                                    }
                                    value={option.additionalCharge}
                                />
                            </Col>
                        </Row>
                    ))}

                    <Row style={{ paddingTop: ".5rem", paddingBottom: ".5rem" }}>
                        <Col xs={12} style={{ textAlign: "right" }}>
                            <Button
                                buttonvariant={variant.Primary}
                                icon={icon.Plus}
                                onClick={() => props.onOptionAdd()}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    </div>
)

interface PriceInputProps {
    value: number; // in pennies
    onChange: (newValue: number) => void; // returns in pennies
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
}

interface PriceInputProps {
    value: number;                       // pennies (only used on mount)
    onChange: (pennies: number) => void; // emit pennies
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
}

export const PriceInput: React.FC<PriceInputProps> = ({
    value,
    onChange,
    placeholder = "Amount",
    className = "form-control",
    style,
}) => {
    const [display, setDisplay] = useState<string>((value / 100).toFixed(2));
    const [isValid, setIsValid] = useState(true);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const strVal = e.target.value.replace(",", ".");
        setDisplay(strVal);

        const pounds = parseFloat(strVal);

        if (!isNaN(pounds)) {
            setIsValid(true);
            onChange(Math.round(pounds * 100)); // emit pennies
        } else {
            setIsValid(false);
            // don't emit anything if invalid
        }
    };

    const handleBlur = () => {
        const pounds = parseFloat(display);
        if (!isNaN(pounds)) {
            setDisplay(pounds.toFixed(2)); // normalise to 2 decimals
            setIsValid(true);
        }
    };

    return (
        <InputGroup>
            <InputGroup.Text>£</InputGroup.Text>
            <Form.Control
                type="text"
                inputMode="decimal"
                className={className}
                style={{
                    ...style,
                    border: isValid ? "1px solid #ccc" : `1px solid ${Color.Red}`, // red border if invalid
                    borderRadius: "6px",
                    padding: "6px 10px",
                    outline: "none",
                    transition: "border 0.2s ease",
                }}
                placeholder={placeholder}
                value={display}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </InputGroup>
    );
};

export interface ProductVariantOverridesFormProps {
    product: ProductDto
    onChange: (key: string, variant: ProductVariantOverride) => void
}

export function getVariantOverride(key: string, overrides?: Map<string, ProductVariantOverride>): ProductVariantOverride | undefined {
    if (!overrides) {
        return
    }

    if (overrides.has(key)) {
        return overrides.get(key)
    }

    const values: string[] = key.split("#")

    const result = Array.from(overrides.values()).filter(value => {
        let isPresent = true
        if (!value.key || !arraysEqual(value.key.split("#"), values)) {
            isPresent = false
        }
        return isPresent
    })

    if (result.length === 0) {
        return
    }

    return result[0]
}

function arraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
        return false;
    }

    return arr1.every((elem) => arr2.includes(elem));
}